import React, { useState } from "react";
import "@/App.css";
import "./styles/sauce-sity-theme.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import InstallPWA from "./components/InstallPWA";
import PushPermissionBanner from "./components/PushPermissionBanner";
import PromoPopup from "./components/PromoPopup";
import MerchPopup from "./components/MerchPopup";
import SplashScreen from "./components/SplashScreen";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Gallery from "./pages/Gallery";
import PickNumberGame from "./pages/PickNumberGame";
import Booking from "./pages/Booking";
import MyAppointments from "./pages/MyAppointments";
import Membership from "./pages/Membership";
import MembershipSuccess from "./pages/MembershipSuccess";
import Rewards from "./pages/Rewards";
import Leaderboard from "./pages/Leaderboard";
import Referrals from "./pages/Referrals";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAppointments from "./pages/admin/AdminAppointments";
import AdminPromotions from "./pages/admin/AdminPromotions";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminPickNumberGame from "./pages/admin/AdminPickNumberGame";
import Admin2FA from "./pages/admin/Admin2FA";
import AdminTeamChat from "./pages/admin/AdminTeamChat";
import AdminHolidays from "./pages/admin/AdminHolidays";
import AdminPasswordReset from "./pages/admin/AdminPasswordReset";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminMerch from "./pages/admin/AdminMerch";
import PointsAdjuster from "./pages/admin/PointsAdjuster";
import AdminPush from "./pages/admin/AdminPush";
import BarberDashboard from "./pages/barber/BarberDashboard";
import EnhancedSchedule from "./pages/barber/EnhancedSchedule";
import BarberProfileFix from "./pages/barber/BarberProfileFix";
import Promotions from "./pages/Promotions";
import Merch from "./pages/Merch";
import SMSTest from "./pages/SMSTest";
import ShareYourStyle from "./pages/ShareYourStyle";
import MyUploads from "./pages/MyUploads";
import LoadingDemo from "./pages/LoadingDemo";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

const ProtectedRoute = ({ children, adminOnly = false, barberOnly = false, staffOnly = false }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white">Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && currentUser.role !== 'admin') {
    return <Navigate to="/" />;
  }

  if (barberOnly && currentUser.role !== 'barber') {
    return <Navigate to="/" />;
  }

  if (staffOnly && !['admin', 'barber'].includes(currentUser.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-black relative overflow-x-hidden max-w-full">
      {/* Faded watermark background logo */}
      <div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_barber-cape-coral/artifacts/krs782np_836.png)',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '50%',
          opacity: 0.03,
          maxWidth: '800px',
          margin: '0 auto',
          left: 0,
          right: 0
        }}
      />
      <div className="relative z-10 overflow-x-hidden">
        <Header />
        <main className="overflow-x-hidden">{children}</main>
        <Footer />
      </div>
    </div>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <AuthProvider>
      {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-center" />
        <InstallPWA />
        <PushPermissionBanner />
        <PromoPopup />
        <MerchPopup />
        <Routes>
          {/* Auth Routes - No Layout */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Public Routes with Layout */}
          <Route path="/" element={<Layout><Home /></Layout>} />
          <Route path="/services" element={<Layout><Services /></Layout>} />
          <Route path="/membership" element={<Layout><Membership /></Layout>} />
          <Route path="/membership/success" element={<MembershipSuccess />} />
          <Route path="/rewards" element={<Layout><Rewards /></Layout>} />
          <Route path="/leaderboard" element={<Layout><Leaderboard /></Layout>} />
          <Route path="/referrals" element={<Layout><Referrals /></Layout>} />
          <Route path="/gallery" element={<Layout><Gallery /></Layout>} />
          <Route path="/pick-number-game" element={<Layout><PickNumberGame /></Layout>} />
          <Route path="/promotions" element={<Layout><Promotions /></Layout>} />
          <Route path="/merch" element={<Layout><Merch /></Layout>} />
          <Route path="/share-your-style" element={<Layout><ShareYourStyle /></Layout>} />
          <Route path="/loading-demo" element={<Layout><LoadingDemo /></Layout>} />
          <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
          <Route path="/terms-conditions" element={<Layout><TermsConditions /></Layout>} />

          {/* Protected Customer Routes */}
          <Route
            path="/booking"
            element={
              <ProtectedRoute>
                <Layout><Booking /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-appointments"
            element={
              <ProtectedRoute>
                <Layout><MyAppointments /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-uploads"
            element={
              <ProtectedRoute>
                <Layout><MyUploads /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Protected Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminDashboard /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/appointments"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminAppointments /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sms-test"
            element={
              <ProtectedRoute adminOnly>
                <Layout><SMSTest /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/promotions"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminPromotions /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/gallery"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminGallery /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pick-number-game"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminPickNumberGame /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/2fa"
            element={
              <ProtectedRoute adminOnly>
                <Layout><Admin2FA /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/team-chat"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminTeamChat /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/holidays"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminHolidays /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/password-reset"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminPasswordReset /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminSettings /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/merch"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminMerch /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff/points-adjust"
            element={
              <ProtectedRoute staffOnly>
                <Layout><PointsAdjuster /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/push"
            element={
              <ProtectedRoute adminOnly>
                <Layout><AdminPush /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Barber Routes */}
          <Route
            path="/barber/dashboard"
            element={
              <ProtectedRoute barberOnly>
                <BarberDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/barber/schedule"
            element={
              <ProtectedRoute barberOnly>
                <EnhancedSchedule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/barber/fix-profile"
            element={
              <ProtectedRoute barberOnly>
                <BarberProfileFix />
              </ProtectedRoute>
            }
          />

        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
